import { AxiosHeaders } from 'axios'
import { camelCase } from 'lodash'
import OpenAPIClientAxios, {
  type AxiosError,
  type Document,
} from 'openapi-client-axios'
import { v4 } from 'uuid'

import { HttpError } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import type { Client as IntegrationsClientSchema } from './client.d'
import definition from './openapi.json'

const INTEGRATIONS_BASE_URL = '/api/v3/integrations/proxy' as const
export const getCurrentSubdomain = () => window.location.hostname.split('.')[0]

/**
 * Ensures that the given URL ends with a trailing slash.
 * This is required for compatibility with the API, which expects URLs to have trailing slashes.
 * @param url - The URL to modify.
 * @returns The URL with a trailing slash.
 */
const ensureTrailingSlash = (url: string | undefined): string | undefined => {
  if (!url) return
  return url.endsWith('/') ? url : `${url}/`
}

const errorInterceptor = (error: AxiosError) => {
  // Use AxiosHeaders.toJSON(true) to convert all values to strings
  const headers = error.response?.headers
  const normalizedHeaders =
    headers instanceof AxiosHeaders ? headers.toJSON(true) : headers || {}

  throw new HttpError(
    error.response?.status ?? -1,
    error.response?.data ?? null,
    normalizedHeaders as Record<string, string | undefined>
  )
}

const integrationsOpenApiClient = new OpenAPIClientAxios({
  definition: definition as Document,
  axiosConfigDefaults: {
    withCredentials: true,
    baseURL: INTEGRATIONS_BASE_URL,
    xsrfCookieName: 'csrftoken',
    xsrfHeaderName: 'X-CSRFToken',
    headers: {
      'X-Procurify-Client': 'web:react',
      'X-Procurify-Tenant': getCurrentSubdomain(),
    },
  },
  transformOperationName: (operation) => camelCase(operation),
}).initSync<IntegrationsClientSchema>()

integrationsOpenApiClient.api.client.interceptors.request.use((request) => {
  request.headers.set('X-Correlation-Id', v4())

  request.url = ensureTrailingSlash(request.url)

  return request
})

integrationsOpenApiClient.api.client.interceptors.response.use(
  undefined,
  errorInterceptor
)

// TODO(PLAT-5597): the companies route and the two Business Central company
// setting keys were added to openapi.json by hand (accounting-integrations
// #2276 and #2271). Regenerate from accounting-integrations main once those
// and #2275 are merged.
export {
  type BusinessCentralCompanyResponse,
  type IntegrationResponse,
  type IntegrationSettingKey,
  type IntegrationSettingResponse,
  type IntegrationSettingRequest,
  type EntitySyncConfigurationResponse,
  type FieldMapping,
  type FieldMappingResponse,
  type SyncResponse,
  type SyncLogResponse,
  type Paths,
} from './client.d'

export { integrationsOpenApiClient as IntegrationsApiClient }
